import React from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';

import { IncidentTypes } from './IncidentTypes';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import LegacyPratusTable from 'components/DesignSystems/PratusTable/LegacyPratusTable';
import { startLoading } from 'reducers/loading/loading.action';
import FormSelectField from '../IAP/formFields/FormSelectField';
import { selectGroup, useAppSelector } from '../../slices/commonSelectors';
import FormTextField from '../IAP/formFields/FormTextField';
import { useNavigate } from 'react-router';
import { useAors } from 'hooks/useAors';
import { useTeamActivation } from 'hooks/incident/useTeamActivation';

import { useCreateIncident } from 'components/IAP/hooks/incidentHooks';
import { updateCurrentIncident } from 'actions/diceActions';

const RESPONSE_WORKFLOWS = {
  ICS: 'ics',
} as const;

type NewIncidentDialogProps = {
  onClose: () => void;
};

type NewIncidentFormValues = {
  incident_name: string;
  response_workflow: (typeof RESPONSE_WORKFLOWS)[keyof typeof RESPONSE_WORKFLOWS];
  sponsor_organization: string;
  activated_teams: string[];
  areas_of_responsibility: string[];
  incident_types: string[];
};

export default function NewIncidentDialog({ onClose }: NewIncidentDialogProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: aors = [], isLoading: aorsIsLoading } = useAors();

  const aorOptions = aors.map((aor) => ({
    value: aor.id,
    label: aor.name,
  }));

  const responseWorkflowOptions = [
    {
      label: 'Incident Command System',
      value: RESPONSE_WORKFLOWS.ICS,
    },
  ];
  const createIncidentMutation = useCreateIncident();

  // Redux selectors
  const groups = useAppSelector((state) => state.app.groups);
  const currentlySelectedGroup = useAppSelector(selectGroup);

  const form = useForm<NewIncidentFormValues>({
    defaultValues: {
      response_workflow: RESPONSE_WORKFLOWS.ICS,
      sponsor_organization: currentlySelectedGroup?.group_guid,
      activated_teams: [],
      areas_of_responsibility: [],
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isValid, isDirty },
  } = form;

  // Watch for selected teams
  const selectedTeams = watch('activated_teams');
  const { icsTeams, activatedUsers } = useTeamActivation(selectedTeams);

  const onNewIncidentSubmit: SubmitHandler<NewIncidentFormValues> = async (
    data
  ) => {
    dispatch(startLoading());

    // Filter out users with incomplete data (Loading...)
    const validActivatedUsers = activatedUsers.filter((user) => {
      const isValid =
        user.name !== 'Loading...' && user.email_address !== 'Loading...';
      if (!isValid) {
        console.warn(
          `⚠️ Skipping user ${user.user_guid} - incomplete data:`,
          user
        );
      }
      return isValid;
    });

    // Prepare activated users for the API
    const activatedUsersForApi = validActivatedUsers.map((user) => ({
      user_guid: user.user_guid,
      role: user.role,
      email_address: user.email_address,
      name: user.name,
    }));

    const newIncidentData = {
      name: data.incident_name,
      group_guid: data.sponsor_organization,
      areas_of_responsibility: data.areas_of_responsibility,
      incident_types: data.incident_types,
      activated_users: activatedUsersForApi,
    };

    try {
      const incident =
        await createIncidentMutation.mutateAsync(newIncidentData);
      dispatch(updateCurrentIncident(incident));
      navigate(`/iap/${incident.id}/initial-response`);
      onClose();
    } catch {
      reset(data);
    }
  };

  // Table columns for activated users
  const tableColumns = [
    {
      dataField: 'name',
      text: 'Name',
      sort: true,
    },
    {
      dataField: 'email_address',
      text: 'Email',
      sort: true,
    },
    {
      dataField: 'role',
      text: 'Role',
      sort: true,
    },
    {
      dataField: 'teams',
      text: 'Teams',
      formatter: (teams: string[]) => teams.join(', '),
    },
  ];

  return (
    <Modal
      show={true}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>New Incident</Modal.Title>
      </Modal.Header>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onNewIncidentSubmit)}>
          <Modal.Body>
            <div className="mb-3">
              <FormTextField
                title={'Name'}
                fieldName={'incident_name'}
                rules={{
                  required: {
                    value: true,
                    message: 'This field is required',
                  },
                }}
              />
            </div>

            <div className="mb-3">
              <FormSelectField
                title={'Incident Type'}
                fieldName={'incident_types'}
                options={IncidentTypes.map((type) => ({
                  value: type,
                  label: type,
                }))}
                rules={{
                  required: {
                    value: true,
                    message: 'This field is required',
                  },
                }}
                isMulti={true}
              />
            </div>
            <div className="row mb-4">
              <FormSelectField
                title={'Area of Responsibility (AOR)'}
                options={aorOptions}
                fieldName={'areas_of_responsibility'}
                isClearable={true}
                disabled={aorsIsLoading}
                isMulti={true}
              />
            </div>
            <div className="row mb-4">
              <FormSelectField
                title={'Response Workflow'}
                fieldName={'response_workflow'}
                options={responseWorkflowOptions}
              />
            </div>
            <div className="mb-3">
              <FormSelectField
                title={'Sponsor Organization'}
                fieldName={'sponsor_organization'}
                options={(groups ?? []).map((group: Group) => ({
                  value: group.group_guid,
                  label: group.group_name,
                }))}
                rules={{
                  required: {
                    value: true,
                    message: 'This field is required',
                  },
                }}
              />
            </div>

            <div className="mb-3">
              <FormSelectField
                title={'Teams to Activate'}
                fieldName={'activated_teams'}
                options={icsTeams.map((team) => ({
                  value: team.id,
                  label: team.name,
                }))}
                isMulti={true}
                placeholder="Select teams to activate (optional)"
              />
              <small className="form-text text-muted">
                Select teams whose members will be activated for this incident.
                You will always be included as the creator.
              </small>
            </div>

            <div className="mb-3">
              <h6>Members to be Activated ({activatedUsers.length})</h6>
              <LegacyPratusTable
                keyField="user_guid"
                columns={tableColumns}
                rows={activatedUsers}
                pagination={false}
                noDataIndication="You will be activated as the incident creator."
              />
            </div>
          </Modal.Body>

          <Modal.Footer>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={onClose}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                type="submit"
                className="button--primary"
                disabled={isSubmitting || (isDirty && !isValid)}
              >
                Create Incident
              </StylishNewButton>
            </div>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
}
